import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import { api } from '../api/client';
import { date } from '../lib/format';

const EMPTY = { email: '', name: '', password: '', role: 'staff', is_active: true };

const METHOD_LABEL = { local: 'Password', microsoft: 'Microsoft', both: 'Microsoft + password' };
const ROLES = [
  { key: 'admin', name: 'Admin' },
  { key: 'accounting', name: 'Accounting' },
  { key: 'staff', name: 'Staff' },
  { key: 'field_staff', name: 'Field Staff' },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.key, r.name]));

export default function Users() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // the row being edited
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api('/users')
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => {
        setRows([]);
        if (e?.status && e.status !== 404) setError('Could not load users.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({ email: row.email, name: row.name || '', password: '', role: row.role, is_active: row.is_active !== false });
    setFormError(null);
    setOpen(true);
  }
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!editing && !form.email.trim()) { setFormError('Email is required.'); return; }
    if (!editing && !form.password) { setFormError('Set an initial password.'); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        const body = { name: form.name || null, role: form.role, is_active: form.is_active };
        if (form.password) body.password = form.password; // optional reset
        await api(`/users/${editing.id}`, { method: 'PUT', body });
      } else {
        await api('/users', {
          method: 'POST',
          body: { email: form.email.trim(), name: form.name || null, password: form.password, role: form.role },
        });
      }
      setOpen(false);
      load();
    } catch (e) {
      setFormError(e?.detail || 'Could not save the user.');
    } finally {
      setSaving(false);
    }
  }

  const columns = [
    { key: 'name', header: 'Name', render: (r) => r.name || '—' },
    { key: 'email', header: 'Email' },
    { key: 'role', header: 'Role', render: (r) => ROLE_LABEL[r.role] || r.role },
    { key: 'auth_method', header: 'Sign-in', render: (r) => METHOD_LABEL[r.auth_method] || r.auth_method },
    { key: 'last_login_at', header: 'Last login', render: (r) => date(r.last_login_at) },
    { key: 'is_active', header: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'cancelled'} /> },
  ];

  return (
    <>
      <PageHeader title="Users" actions={<button className="btn btn--primary" onClick={openNew}>New user</button>} />
      <section className="panel">
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          getRowKey={(r) => r.id}
          onRowClick={openEdit}
          empty="No users yet."
        />
      </section>

      <Drawer
        open={open}
        title={editing ? 'Edit user' : 'New user'}
        onClose={() => !saving && setOpen(false)}
        footer={
          <>
            <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create user'}
            </button>
          </>
        }
      >
        {formError && <div className="form-error">{formError}</div>}

        <div className="field">
          <label>Email{!editing && ' *'}</label>
          {editing
            ? <div className="ro-value">{form.email}</div>
            : <input type="email" value={form.email} autoFocus onChange={(e) => set('email', e.target.value)} />}
        </div>
        <div className="field">
          <label>Name</label>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Role</label>
            <select value={form.role} onChange={(e) => set('role', e.target.value)}>
              {ROLES.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>{editing ? 'Reset password' : 'Password *'}</label>
            <input type="password" value={form.password} placeholder={editing ? 'Leave blank to keep' : ''}
              onChange={(e) => set('password', e.target.value)} />
          </div>
        </div>
        {editing && (
          <>
            <label className="check">
              <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
              Active
            </label>
            {editing.auth_method === 'microsoft' && (
              <p className="hint" style={{ marginTop: 8 }}>
                This is a Microsoft account. Setting a password also enables password sign-in.
              </p>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
